import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    OnDestroy,
} from '@angular/core';
import { DigitalBusinessCard } from '../../dbc.model';
import { DbcService } from '../../../global-services/dbc.service';
import { from, Observable, of, Subject, takeUntil } from 'rxjs';
import { NativeShareService } from '../../../global-services/navigator-share.service';
import {
    AMPLITUDE_EVENT_CATEGORIES,
    AMPLITUDE_EVENTS,
    AmplitudeService,
} from '../../../global-services/amplitude.service';
import { AuthService } from '../../../global-services/auth.service';
import { filter, map, switchMap } from 'rxjs/operators';
import { IndexedDbService } from '../../../global-services/indexed-db.service';
import { OBJECT_STORES } from '../../../index-db';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'share-screen-component',
    templateUrl: './share-screen.component.html',
    styleUrl: './share-screen.component.scss',
    standalone: false
})
export class ShareScreenComponent implements OnInit, OnChanges, OnDestroy {
    @Input() selectedDbc: DigitalBusinessCard = null;

    @Output()
    closeScreen: EventEmitter<any> = new EventEmitter();

    @Output()
    shareCancelled: EventEmitter<any> = new EventEmitter();

    qrCode: any = '';
    googleWalletPassBaseLink: string = '';
    appleWalletPassBaseLink: string = '';
    googleWalletPassLink: string = '';
    appleWalletPassLink: string = '';
    currentOrgWallet: {} = {};
    isIos: boolean = false;
    slug: string = '';
    ngUnsubscribe: Subject<any> = new Subject<any>();

    constructor(
        private dbcService: DbcService,
        private nativeShareService: NativeShareService,
        private readonly amplitude: AmplitudeService,
        private readonly authService: AuthService,
        private readonly indexDbService: IndexedDbService
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes['selectedDbc'] && changes['selectedDbc'].currentValue) {
            this.selectedDbc = changes['selectedDbc'].currentValue;
            this.generateQrCode();
        }
    }

    ngOnInit() {
        this.getWalletPassBaseLinks();
        this.generateQrCode();

        this.authService.checkDeviceOs$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((deviceOs: string) => {
                this.isIos = deviceOs === 'iOS';
            });
        this.getWalletPassTemplate().subscribe((wallet) => {
            this.currentOrgWallet = wallet;

            if (this.isIos) {
                this.getAppleWalletPassDetails(this.currentOrgWallet);
            } else {
                this.getGoogleWalletPassDetails(this.currentOrgWallet);
            }
        });
    }

    getWalletPassTemplate(): Observable<any> {
        if (navigator.onLine) {
            return this.authService.isAuthenticated$.pipe(
                filter((val) => val !== null),
                switchMap((isAuthenticated) => {
                    if (isAuthenticated) {
                        return of(
                            this.authService.getCurrentOrganization()
                                .wallet_pass_template
                        );
                    } else {
                        return this.fetchWalletPassFromIndexedDB();
                    }
                })
            );
        } else {
            return this.fetchWalletPassFromIndexedDB();
        }
    }

    fetchWalletPassFromIndexedDB(): Observable<any> {
        return this.indexDbService
            .getAll(OBJECT_STORES.ORGANIZATIONS)
            .pipe(
                map((orgs) =>
                    orgs.length > 0 ? orgs[0]?.wallet_pass_template : null
                )
            );
    }

    getWalletPassBaseLinks() {
        const url = this.selectedDbc?.url;
        if (url) {
            const slug = url.split('/').pop();
            this.appleWalletPassBaseLink = `${environment.appleWalletPassBaseUrl}${slug}`;
            this.googleWalletPassBaseLink = `${environment.googleWalletPassBaseUrl}${slug}`;
        }
    }

    getGoogleWalletPassDetails(currentOrgWallet: {} | null | undefined) {
        if (!currentOrgWallet || Object.keys(currentOrgWallet).length === 0) {
            console.warn('No wallet_pass_template found!');
            this.googleWalletPassLink = this.googleWalletPassBaseLink;
            return;
        }

        const background = currentOrgWallet['google_wallet_pass_background'];
        const logo = currentOrgWallet['google_wallet_pass_logo_id'];
        const heroImage = currentOrgWallet['google_wallet_pass_hero_image_id'];
        const params: Record<string, string> = {};

        if (background) params['background'] = background;
        if (logo) {
            params['logourl'] = logo;
        } else if (this.selectedDbc?.logo_url && !logo) {
            params['dbc_logo_url'] = 'true';
        }

        if (heroImage) params['heroimageurl'] = heroImage;

        const queryString = new URLSearchParams(params).toString();
        this.googleWalletPassLink = queryString
            ? `${this.googleWalletPassBaseLink}?${queryString}`
            : this.googleWalletPassBaseLink;
    }

    getAppleWalletPassDetails(currentOrgWallet: {} | null | undefined) {
        if (!currentOrgWallet || Object.keys(currentOrgWallet).length === 0) {
            console.warn('No wallet_pass_template found!');
            this.appleWalletPassLink = this.appleWalletPassBaseLink;
            return;
        }

        const background = currentOrgWallet['apple_wallet_pass_background'];
        const foreground = currentOrgWallet['apple_wallet_pass_foreground'];
        const logo = currentOrgWallet['apple_wallet_pass_logo_id'];

        const params: Record<string, string> = {};

        if (logo) {
            params['logoId'] = logo;
        } else if (this.selectedDbc?.logo_url && !logo) {
            params['showDBCLogo'] = 'True';
        }

        if (background) params['background'] = background;
        if (foreground) params['foreground'] = foreground;

        const queryString = new URLSearchParams(params).toString();
        this.appleWalletPassLink = queryString
            ? `${this.appleWalletPassBaseLink}?${queryString}`
            : this.appleWalletPassBaseLink;
    }

    generateQrCode() {
        if (!this.selectedDbc?.__qr_code_url__) {
            const ob = from(
                this.dbcService.generateDBCQrCode(this.selectedDbc)
            );
            ob.subscribe((qrCode) => {
                this.qrCode = qrCode;
            });
        } else {
            this.qrCode = this.selectedDbc.__qr_code_url__;
        }
    }

    async shareByOsOptions() {
        this.closeShareScreen();
        this.amplitude.logEvent(
            AMPLITUDE_EVENT_CATEGORIES.Usage,
            AMPLITUDE_EVENTS.SHARE_CARD_LINK_CLICKED
        );

        try {
            await this.nativeShareService.shareUrl(
                this.selectedDbc.url,
                this.selectedDbc.name,
                ''
            );
        } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') {
                this.onShareCancel(); // Handle canceled or failed sharing
            } else {
                console.error('Share failed due to another error', error);
            }
        }
    }

    closeShareScreen() {
        this.closeScreen.emit();
    }

    onShareCancel() {
        this.shareCancelled.emit();
    }

    openWalletPassLink(url: string): void {
        if (url && !this.isIos) {
            window.open(url, '_blank', 'noopener,noreferrer');
        } else if (url && this.isIos) {
            window.location.assign(url);
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(null);
        this.ngUnsubscribe.complete();
    }
}
